/**
 * AvailabilityDialog: the office's weekly call note for one care manager
 * or SPICE therapist, opened from the availability table's "More". In
 * this order: On leave this week; the Weekly call note (no placeholder,
 * at least eight lines at rest, growing with the text up to most of the
 * window and then scrolling inside, with a live count against the
 * 4,000-character cap); and, for a care manager, Clients mentioned
 * (ClientsMentionedField: a search over their caseload, each pick tagged
 * On hospice, Passed away or Other). Saves through the existing
 * PUT /workspace/capacity/{rosterId}/availability (manage_staff_availability),
 * which carries the mentions in the same save only when they changed.
 * On hospice and Passed away need their date, no later than today: a
 * save without one shows the sentence on that client and sends nothing.
 *
 * The stance on new clients (new_client_stance) is not edited here: the
 * table's own "New clients" toggle owns it. This dialog carries the
 * stored stance straight through on save, untouched, so opening "More"
 * to record a leave date or a note can never silently change it. A week
 * with no row saves 'open', the stance a week with no row reads as.
 *
 * It carries the stored override the server sent on the row. A row sent
 * without it has nothing to carry through, so the dialog offers no Save
 * and never writes over what is stored.
 *
 * ONE EDITOR AT A TIME (0216). Opening takes the week's edit lease
 * (useAvailabilityLease). While someone else holds it, the fields are
 * read-only and one line names who has it open; when they close, the next
 * renewal takes it and the dialog becomes editable, showing the row as it
 * now stands. After ten minutes with no typing or clicks the dialog lets
 * go ("Editing paused."), keeping what was typed, and Continue editing
 * takes the lease again. Every save names the version it was opened at,
 * so a save over a newer change is refused whatever the lock showed.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { useAvailabilityLease } from '@/lib/availabilityLease';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { FLD_LABEL, FLD_TEXTAREA_GROW } from '@/components/primitives/modalFormStyles';
import {
  ClientsMentionedField,
  mentionDateProblem,
  type CaseloadPick,
  type MentionDraft,
} from '@/components/capacity/ClientsMentionedField';
import { AVAILABILITY_NOTES_MAX, weekLabel } from '@/lib/capacity';
import { practiceTodayIso } from '@/lib/practiceDate';
import type { AvailabilityRowWire, CallMentionWire, ClientCallMentionInput, StaffAvailabilityInput } from '@/lib/capacityApi';

/** The note box at rest: eight lines. */
const NOTE_ROWS = 8;

/** The note box grows with its text, up to this much of the window, then
 *  scrolls inside. In an environment with no layout the height is left
 *  alone. */
function fitNoteBox(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
}

/** A stored mention as the dialog holds it: its date is the record's. */
function draftOf(m: CallMentionWire): MentionDraft {
  const date = m.tag === 'hospice' ? m.on_hospice_since : m.tag === 'passed_away' ? m.date_of_death : null;
  return { client_id: m.client_id, display_name: m.display_name, tag: m.tag, date: date ?? '' };
}

function toInput(d: MentionDraft): ClientCallMentionInput {
  return { client_id: d.client_id, tag: d.tag, date: d.tag === 'other' || d.date === '' ? null : d.date };
}

function sameMentions(a: MentionDraft[], b: MentionDraft[]): boolean {
  const key = (xs: MentionDraft[]) =>
    JSON.stringify(xs.map(toInput).sort((x, y) => (x.client_id < y.client_id ? -1 : 1)));
  return key(a) === key(b);
}

export interface AvailabilityDialogProps {
  row: AvailabilityRowWire;
  weekStart: string;
  busy: boolean;
  error: string | null;
  onSave: (input: StaffAvailabilityInput) => void;
  onClose: () => void;
  /** The clients already named on this week's call for this person. Left
   *  out (a SPICE therapist, or the mentions not read), there is no
   *  Clients mentioned and a save sends none. */
  mentions?: CallMentionWire[];
  /** Their caseload, for the search. */
  caseload?: CaseloadPick[];
  /** Whether this seat sets a client's status (manage_client_identity):
   *  On hospice and Passed away are greyed without it. */
  canSetRecord?: boolean;
  /** What the record holds for a client and tag, to open a new pick on. */
  recordDate?: (clientId: string, tag: 'hospice' | 'passed_away') => string | null;
}

export function AvailabilityDialog({
  row,
  weekStart,
  busy,
  error,
  onSave,
  onClose,
  mentions,
  caseload = [],
  canSetRecord = false,
  recordDate = () => null,
}: AvailabilityDialogProps): ReactNode {
  const a = row.availability;
  const sent = a !== undefined;
  const { tenantSlug } = useTenant();
  const lease = useAvailabilityLease(tenantSlug, row.roster_id, weekStart, sent);
  const editable = lease.state.kind === 'mine' || lease.state.kind === 'pending' || lease.state.kind === 'unavailable';
  const [onLeave, setOnLeave] = useState<boolean>(a?.on_leave ?? false);
  const [notes, setNotes] = useState<string>(a?.notes ?? '');
  const edited = useRef(false);
  const noteBox = useRef<HTMLTextAreaElement | null>(null);
  const storedDrafts = (mentions ?? []).map(draftOf);
  const [drafts, setDrafts] = useState<MentionDraft[]>(storedDrafts);
  const [showProblems, setShowProblems] = useState(false);
  const today = practiceTodayIso();
  const mentionsKey = JSON.stringify(storedDrafts.map(toInput));

  useLayoutEffect(() => {
    fitNoteBox(noteBox.current);
  }, [notes]);

  // The stored mentions arrive (or change) while nothing was typed: show them.
  useEffect(() => {
    if (edited.current) return;
    setDrafts(storedDrafts);
  }, [mentionsKey]);

  // A dialog that only read (someone else had it open) shows the row as it
  // now stands when that row changes: nothing typed is overwritten.
  const version = a === undefined || a === null ? null : (a.version ?? 0);
  useEffect(() => {
    if (edited.current) return;
    setOnLeave(a?.on_leave ?? false);
    setNotes(a?.notes ?? '');
  }, [version]);

  const submit = (): void => {
    if (!sent || !editable) return;
    const mentionsChanged = mentions !== undefined && !sameMentions(drafts, storedDrafts);
    if (mentionsChanged && drafts.some((d) => mentionDateProblem(d, today) !== null)) {
      setShowProblems(true);
      return;
    }
    onSave({
      week_start: weekStart,
      on_leave: onLeave,
      // The "New clients" toggle owns the stance; carry it through
      // unedited. A week with no row reads as open, and saves as open.
      new_client_stance: a === null ? 'open' : a.new_client_stance,
      // No surface records free hours any more (a target is an average,
      // not a cap to fill); a figure stored earlier is dropped on save.
      capacity_hours: null,
      notes: notes.trim() === '' ? null : notes.trim(),
      // The version this dialog shows; a newer save makes this one refused.
      expected_version: version,
      // The whole set, only when it changed: an untouched list leaves the
      // stored mentions, and the record, alone.
      ...(mentionsChanged ? { mentions: drafts.map(toInput) } : {}),
    });
  };

  return (
    <Dialog
      title={`${row.full_name}: week of ${weekLabel(weekStart)}`}
      onClose={onClose}
      size={mentions !== undefined ? 'xl' : 'md'}
      testId="availability-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          {sent && (
            <Button variant="primary" onClick={submit} loading={busy} disabled={!editable} data-testid="availability-save">Save</Button>
          )}
        </>
      }
    >
      {lease.state.kind === 'held' && (
        <p role="status" className="text-sm text-ink mb-3" data-testid="availability-lease-held">
          {`${lease.state.holderName} has this open. You can read it here; saving is off until they close it.`}
        </p>
      )}
      {lease.state.kind === 'paused' && (
        <div role="status" className="flex flex-wrap items-center gap-3 mb-3" data-testid="availability-paused">
          <p className="text-sm text-ink">Editing paused.</p>
          <Button variant="secondary" size="sm" onClick={lease.continueEditing} data-testid="availability-continue-editing">
            Continue editing
          </Button>
        </div>
      )}
      <div
        className="flex flex-col gap-4 mb-4"
        onKeyDown={lease.noteActivity}
        onPointerDown={lease.noteActivity}
      >
        <label className="flex items-center gap-2 text-sm text-ink cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onLeave}
            disabled={!editable}
            onChange={(e) => {
              edited.current = true;
              setOnLeave(e.target.checked);
            }}
            data-testid="availability-on-leave"
            className="h-[16px] w-[16px] accent-[var(--color-sage-800)]"
          />
          On leave this week
        </label>
        <div>
          <label htmlFor="availability-notes" className={FLD_LABEL}>Weekly call note</label>
          <textarea
            id="availability-notes"
            ref={noteBox}
            rows={NOTE_ROWS}
            value={notes}
            readOnly={!editable}
            onChange={(e) => {
              edited.current = true;
              setNotes(e.target.value);
            }}
            data-testid="availability-notes"
            aria-describedby="availability-notes-count"
            className={`${FLD_TEXTAREA_GROW} max-h-[60vh] resize-none overflow-y-auto leading-[1.45]`}
          />
          {/* A live count against the cap the server keeps; past it the
              count turns pepper and the save is refused with the cap. */}
          <p
            id="availability-notes-count"
            data-testid="availability-notes-count"
            className={`mt-1 text-xs ${notes.length > AVAILABILITY_NOTES_MAX ? 'text-pepper' : 'text-ink-soft'}`}
          >
            {`${notes.length.toLocaleString('en-US')} of ${AVAILABILITY_NOTES_MAX.toLocaleString('en-US')} characters`}
          </p>
        </div>
        {mentions !== undefined && (
          <ClientsMentionedField
            caseload={caseload}
            value={drafts}
            onChange={(next) => {
              edited.current = true;
              setShowProblems(false);
              setDrafts(next);
            }}
            canSetRecord={canSetRecord}
            readOnly={!editable}
            showProblems={showProblems}
            today={today}
            recordDate={recordDate}
          />
        )}
        {error && (
          <p role="alert" className="text-sm text-danger" data-testid="availability-error">{error}</p>
        )}
      </div>
    </Dialog>
  );
}
